<script setup>
import { ref, computed } from 'vue';
import questionsData from '@/lib/GlobalInvestQuestions.json';

const props = defineProps({
  initialAnswers: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false }
});

const emit = defineEmits(['complete']);

const questions = ref(questionsData.questions || []);
const localAnswers = ref({ ...props.initialAnswers });

const currentQuestionIndex = ref(0);
const currentQuestion = computed(() => questions.value[currentQuestionIndex.value]);

const progressPercent = computed(() => {
  const answeredCount = questions.value.filter(q => {
    const answer = localAnswers.value[q.id];
    if (q.type === 'multi') {
      return answer && Array.isArray(answer) && answer.length > 0;
    }
    return !!answer;
  }).length;
  return Math.round((answeredCount / questions.value.length) * 100);
});

const isCurrentQuestionAnswered = computed(() => {
  const answer = localAnswers.value[currentQuestion.value?.id];
  if (currentQuestion.value?.type === 'multi') {
    return answer && Array.isArray(answer) && answer.length > 0;
  }
  return !!answer;
});

const isAllAnswered = computed(() => {
  return questions.value.every(q => {
    const answer = localAnswers.value[q.id];
    if (q.type === 'multi') {
      return answer && Array.isArray(answer) && answer.length > 0;
    }
    return !!answer;
  });
});

const handleMultiSelect = (questionId, optionId) => {
  if (!localAnswers.value[questionId]) {
    localAnswers.value[questionId] = [];
  }
  const index = localAnswers.value[questionId].indexOf(optionId);
  if (index > -1) {
    localAnswers.value[questionId].splice(index, 1);
  } else {
    localAnswers.value[questionId].push(optionId);
  }
};

const handleSingleSelect = (questionId, optionId) => {
  localAnswers.value[questionId] = optionId;
};

const isOptionSelected = (questionId, optionId) => {
  const answer = localAnswers.value[questionId];
  if (Array.isArray(answer)) {
    return answer.includes(optionId);
  }
  return answer === optionId;
};

const goNext = () => {
  if (currentQuestionIndex.value < questions.value.length - 1) {
    currentQuestionIndex.value++;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const goPrev = () => {
  if (currentQuestionIndex.value > 0) {
    currentQuestionIndex.value--;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const handleSubmit = () => {
  if (!isAllAnswered.value) return;

  // Convert answers to API format
  const apiPayload = {
    answers: {}
  };

  questions.value.forEach(q => {
    apiPayload.answers[q.id] = localAnswers.value[q.id];
  });

  emit('complete', apiPayload);
};
</script>

<template>
  <div class="gi-questionnaire-root">
    <div class="gi-header">
      <div class="progress-wrap">
        <span class="progress-text">{{ progressPercent }}% complete</span>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }" />
        </div>
      </div>
      <div class="question-counter">
        Question {{ currentQuestionIndex + 1 }} of {{ questions.length }}
      </div>
    </div>

    <div v-if="currentQuestion" class="question-card">
      <h3 class="question-text">{{ currentQuestion.text }}</h3>

      <div class="options-list">
        <div
          v-for="option in currentQuestion.options"
          :key="option.id"
          :class="['option-item', { selected: isOptionSelected(currentQuestion.id, option.id) }]"
          @click="currentQuestion.type === 'multi' ? handleMultiSelect(currentQuestion.id, option.id) : handleSingleSelect(currentQuestion.id, option.id)"
        >
          <div class="option-checkbox">
            <i v-if="isOptionSelected(currentQuestion.id, option.id)" class="pi pi-check"></i>
          </div>
          <span class="option-label">{{ option.label }}</span>
        </div>
      </div>

      <p v-if="currentQuestion.type === 'multi'" class="hint-text">
        💡 You can select multiple options
      </p>
    </div>

    <div class="nav-actions">
      <button
        class="btn-secondary"
        type="button"
        @click="goPrev"
        :disabled="currentQuestionIndex === 0 || disabled"
      >
        ← Previous
      </button>
      <button
        v-if="currentQuestionIndex < questions.length - 1"
        class="btn-primary"
        type="button"
        @click="goNext"
        :disabled="!isCurrentQuestionAnswered || disabled"
      >
        Next question →
      </button>
      <button
        v-else
        class="btn-primary"
        type="button"
        @click="handleSubmit"
        :disabled="!isAllAnswered || disabled"
      >
        Review answers
      </button>
    </div>
  </div>
</template>

<style scoped>
.gi-questionnaire-root {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gi-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.progress-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.progress-text {
  font-size: 0.78rem;
  color: #868e96;
  font-weight: 600;
}

.progress-bar {
  height: 6px;
  border-radius: 999px;
  background: #e9ecef;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #2563eb);
  transition: width 0.3s ease;
}

.question-counter {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.question-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 24px;
  border: 2px solid #dee2e6;
}

.question-text {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: white;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.option-item:hover {
  border-color: #3b82f6;
  background: #f8f9fa;
}

.option-item.selected {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.option-checkbox {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 2px solid #ced4da;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: white;
  transition: all 0.2s ease;
}

.option-item.selected .option-checkbox {
  background: #3b82f6;
  border-color: #3b82f6;
}

.option-checkbox i {
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
}

.option-label {
  font-size: 0.9rem;
  color: #343a40;
  font-weight: 500;
  line-height: 1.4;
}

.hint-text {
  margin: 12px 0 0 0;
  font-size: 0.8rem;
  color: #6c757d;
  font-style: italic;
}

.nav-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
}

.btn-primary,
.btn-secondary {
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.btn-secondary {
  background: #f1f3f5;
  color: #343a40;
}

.btn-secondary:hover:not(:disabled) {
  background: #e9ecef;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 768px) {
  .gi-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .question-card {
    padding: 18px;
  }

  .question-text {
    font-size: 1rem;
  }

  .nav-actions {
    flex-direction: column-reverse;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>
